import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product } from '@/api/products';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { useDirectSaleCheckout } from '../useDirectSaleCheckout';
import { resolveCartAction, type CartFacts } from './cart-action';
import type { SizePickerNotice } from './DirectSaleSizePicker';

/** What the slot above the button says after a click, besides errors. */
export type CartFeedback = 'unavailable' | 'handed-over';

interface UseDesktopCartActionArgs {
  product: Product;
  /** The sizes the chosen colour offers. */
  sizes: { id: string; name: string }[];
  /** The pieces chosen among those sizes. */
  pieces: number;
  /** See `DirectSaleDesktopSidebar`: asked first on every click. */
  prepareCart?: () => boolean;
  /** The click came without a design: the parent opens the start dialog. */
  onRequestStart: () => void;
  /** The click came without a size: the column opens its size list. */
  onRequestSize: () => void;
}

/**
 * The cart button of the desktop purchase column as a state machine.
 *
 * The button is always active. What a click does is decided by
 * `resolveCartAction`; this hook only carries it out and names what is
 * missing.
 *
 * It shares `useDirectSaleCheckout` and its switch with the phone's bar,
 * which is mounted (hidden) on the desktop too: same query key, one
 * request, one amount.
 */
export function useDesktopCartAction({
  product,
  sizes,
  pieces,
  prepareCart,
  onRequestStart,
  onRequestSize,
}: UseDesktopCartActionArgs) {
  const { t } = useTranslation();
  const colorId = useDirectSaleStore((s) => s.colorId);
  const quantities = useDirectSaleStore((s) => s.quantities);
  const decoratedPositions = useDirectSaleStore((s) => s.decoratedPositions);

  const [notice, setNotice] = useState<SizePickerNotice | null>(null);
  const [feedback, setFeedback] = useState<CartFeedback | null>(null);
  /**
   * A click that arrived before its price, as the selection it was made
   * for: it buys once the quote is there, and only that selection.
   */
  const [waitingFor, setWaitingFor] = useState<number | null>(null);

  const hasDecoration = decoratedPositions.length > 0;
  const { quote, gallery, handleCheckout, isSubmitting, checkoutError } =
    useDirectSaleCheckout(product, { enabled: hasDecoration });

  const quoteState: CartFacts['quote'] = quote.isError
    ? 'error'
    : quote.data && !quote.isPlaceholderData
      ? 'ready'
      : 'loading';

  // What was said about one configuration says nothing about the next: the
  // confirmation, the "unavailable" line and a click that is still waiting
  // all end with the selection they belonged to.
  // Compared by content: the design step writes a new list of decorated
  // positions with every canvas save, also when nothing about them changed —
  // that must not drop a click that is waiting for its price.
  const decoratedKey = JSON.stringify(decoratedPositions);
  const quantitiesKey = JSON.stringify(quantities);
  const selectionVersion = useRef(0);
  useEffect(() => {
    selectionVersion.current += 1;
    setFeedback(null);
    setWaitingFor(null);
  }, [quantitiesKey, colorId, decoratedKey]);

  const runCheckout = async () => {
    const version = selectionVersion.current;
    const outcome = await handleCheckout();
    // A test link or a failed handover already told the customer by toast,
    // a failed commit through `checkoutError`. Only a real delivery is
    // confirmed — and only if the customer has not moved on meanwhile.
    if (outcome === 'delivered' && version === selectionVersion.current) {
      setFeedback('handed-over');
    }
  };
  const runCheckoutRef = useRef(runCheckout);
  runCheckoutRef.current = runCheckout;

  useEffect(() => {
    if (waitingFor === null || quoteState === 'loading') return;
    setWaitingFor(null);
    // A failed quote shows its reason in the slot by itself.
    if (quoteState === 'ready' && waitingFor === selectionVersion.current) {
      void runCheckoutRef.current();
    }
  }, [waitingFor, quoteState]);

  const handleCartClick = () => {
    // What is bought is what the customer sees: a text still being typed
    // is only stored once the typing ends, and a placeholder left untouched
    // is no design at all.
    const hasDesign = prepareCart ? prepareCart() : hasDecoration;
    const action = resolveCartAction({
      hasDesign,
      sizeCount: sizes.length,
      pieces,
      quote: quoteState,
      isSubmitting,
    });
    if (action === 'none') return;
    setFeedback(null);
    switch (action) {
      case 'open-start':
        onRequestStart();
        break;
      case 'unavailable':
        setFeedback('unavailable');
        break;
      case 'require-size':
        onRequestSize();
        setNotice({
          kind: 'required',
          text: t('directSale.desktop.sizeRequired'),
        });
        break;
      case 'wait':
        setWaitingFor(selectionVersion.current);
        break;
      case 'show-quote-error':
        // Already in the slot: a failed quote is shown without a click.
        break;
      case 'checkout':
        void runCheckout();
        break;
    }
  };

  return {
    quote,
    gallery,
    isSubmitting,
    checkoutError,
    hasDecoration,
    /** A click is waiting for its price. */
    waiting: waitingFor !== null,
    feedback,
    /**
     * The line under the size picker. The request for a size is set here;
     * the column sets and clears the list of removed sizes.
     */
    notice,
    setNotice,
    handleCartClick,
  };
}
